import { useEffect, useRef, useState, useCallback } from "react";
import moment from "moment";
import jwt_decode from "jwt-decode";
import axios from "../pages/api/axios";

const fmtDate = (d) =>
  d && d !== "" ? moment(d).format("YYYY-MM-DD") : "";

const endpointFor = (type) => {
  if (type === "ticket2") return "/ticket/dashboardfilter2";
  if (type === "ticket3") return "/ticket/dashboardfilter3";
  if (type === "ticket4") return "/ticket/dashboardfilter4";
  return `/${type}/dashboardfilter`;
};

const decodeUser = () => {
  try {
    return jwt_decode(window.localStorage.getItem("accessToken"));
  } catch (e) {
    return {};
  }
};

// Simple in-memory cache + in-flight dedupe so multiple consumers (charts +
// KPI tiles) hitting the same endpoint with the same filters share one
// network request. Keyed by URL + serialized params.
const cache = new Map();
const inflight = new Map();
const TTL_MS = 30_000;

const keyFor = (url, params) => `${url}?${JSON.stringify(params)}`;

const fetchSharedData = (url, params, cancelToken) => {
  const k = keyFor(url, params);
  const cached = cache.get(k);
  if (cached && Date.now() - cached.t < TTL_MS) {
    return Promise.resolve(cached.data);
  }
  const pending = inflight.get(k);
  if (pending) return pending;

  const promise = axios
    .get(url, { params, cancelToken })
    .then((response) => {
      const data = (response && response.data && response.data.data) || [];
      cache.set(k, { data, t: Date.now() });
      inflight.delete(k);
      return data;
    })
    .catch((err) => {
      inflight.delete(k);
      throw err;
    });

  inflight.set(k, promise);
  return promise;
};

export const invalidateDashboardCache = () => {
  cache.clear();
};

export const useDashboardData = (type, filters) => {
  const {
    selectedStartDate = "",
    selectedEndDate = "",
    propertyManagerID = "",
    selectedProperty = "",
  } = filters || {};

  const [state, setState] = useState({
    data: [],
    loading: false,
    error: null,
  });

  const abortRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (type === "rent") {
      setState({ data: [], loading: false, error: null });
      return;
    }

    const startDate = fmtDate(selectedStartDate);
    const endDate = fmtDate(selectedEndDate);

    if (startDate === "" && endDate !== "") {
      return;
    }

    const decode = decodeUser();
    const propertyId =
      selectedProperty && selectedProperty.value
        ? selectedProperty.value
        : "";

    const url = endpointFor(type);
    const params = {
      filterBy: "barChart",
      startDate,
      endDate,
      propertyManagerID: propertyManagerID || "",
      role: decode.role || "",
      domain: decode.domain || "",
      propertyId,
    };

    if (abortRef.current) abortRef.current.cancel("superseded");
    const source = axios.CancelToken
      ? axios.CancelToken.source()
      : { cancel: () => {}, token: undefined };
    abortRef.current = source;

    setState((s) => ({ ...s, loading: true, error: null }));

    try {
      const data = await fetchSharedData(url, params, source.token);
      setState({ data, loading: false, error: null });
    } catch (err) {
      if (axios.isCancel && axios.isCancel(err)) return;
      setState({ data: [], loading: false, error: err.message || "Request failed" });
    }
  }, [type, selectedStartDate, selectedEndDate, propertyManagerID, selectedProperty]);

  useEffect(() => {
    fetchData();
    return () => {
      if (abortRef.current) abortRef.current.cancel("unmount");
    };
  }, [fetchData]);

  return state;
};

export default useDashboardData;
